import {BaseCard} from "../../Components/Cards/BaseCard";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {BlackButton} from "../../Components/Buttons/BlackButton";
import {SelectInput} from "../../Components/Form/SelectInput";
import {createColumnHelper} from "@tanstack/react-table";
import {truncateString} from "../../Utils/StringHelper";
import {Link} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowRight} from "@fortawesome/free-solid-svg-icons";
import {TanStackTable} from "../../Components/Tables/TanStackTable";
import * as React from "react";
import {useBoundStore} from "../../Store/store";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import {AdminCreateShiftDialog} from "../../Components/Dialogs/Shifts/AdminCreateShiftDialog";
import dayjs from "dayjs";
import {DateTimePickerInput} from "../../Components/Form/DateTimePickerInput";
import toast from "react-hot-toast";

export const Shifts = () => {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false)

    const customers = useBoundStore((state) => state.customers) || [];
    const getAllCustomers = useBoundStore((state) => state.getAllCustomers);
    const shifts = useBoundStore((state) => state.shifts) || [];
    const shiftsPaginationMeta = useBoundStore((state) => state.shiftsPaginationMeta);
    const getMyShifts = useBoundStore((state) => state.getMyShifts);

    const urlParams = new URLSearchParams(window.location.search);
    const customerIdFromUrl = urlParams.get('customer_id');
    const [customerBranches, setCustomerBranches] = useState([]);
    const [isLoadingCustomers, setIsLoadingCustomers] = useState(true);
    const [hasInitialized, setHasInitialized] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortBy, setSortBy] = useState('start_time');
    const [sortOrder, setSortOrder] = useState('asc');

    const schema = yup.object({
        customer_id: yup.mixed().nullable(),
        start_time: yup.date().nullable(),
        end_time: yup.date().nullable(),
        branch_id: yup.mixed().nullable(),
        statuten_filter: yup.array().nullable(),
    }).required();

    const statutenOptions = [
        {id: 1, value: 'freelance', label: t('statuten.freelance')},
        {id: 2, value: 'interim', label: t('statuten.interim')},
        {id: 3, value: 'student', label: t('statuten.student')},
        {id: 4, value: 'flexi_job', label: t('statuten.flexi_job')},
    ];

    const getStoredValue = (key) => {
        if (typeof window === 'undefined') return null;
        const value = localStorage.getItem(key);
        if (value && !isNaN(value)) {
            return Number(value);
        }
        return value || null;
    };

    const getStoredDate = (key) => {
        if (typeof window === 'undefined') return null;
        const stored = localStorage.getItem(key);
        return stored ? new Date(stored) : null;
    };

    const {control, setValue, getValues, watch, reset, register, formState: {errors}} = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            customer_id: customerIdFromUrl || getStoredValue('admin_shifts_customer_id'),
            start_time: getStoredDate('admin_shifts_start_time') ?? dayjs().startOf('day').toDate(),
            end_time: getStoredDate('admin_shifts_end_time'),
            branch_id: getStoredValue('admin_shifts_branch_id'),
            statuten_filter: [],
        }
    });

    const selectedCustomer = watch('customer_id');
    const startTime = watch('start_time');
    const endTime = watch('end_time');
    const branchId = watch('branch_id');
    const statutenFilter = watch('statuten_filter');

    useEffect(() => {
        if (customerIdFromUrl && !hasInitialized) {
            localStorage.removeItem('admin_shifts_customer_id');
            localStorage.removeItem('admin_shifts_start_time');
            localStorage.removeItem('admin_shifts_end_time');
            localStorage.removeItem('admin_shifts_branch_id');
            reset({
                customer_id: customerIdFromUrl,
                start_time: dayjs().startOf('day').toDate(),
                end_time: null,
                branch_id: null,
            });
            setHasInitialized(true);
        }
    }, [customerIdFromUrl, hasInitialized, reset]);


    useEffect(() => {
        setIsLoadingCustomers(true);
        getAllCustomers(null, null, {
            "nopaginate": true
        }).finally(() => {
            setIsLoadingCustomers(false);
        });
    }, [getAllCustomers]);

    useEffect(() => {
        if (selectedCustomer !== undefined) {
            if (selectedCustomer === null || selectedCustomer === '') {
                localStorage.removeItem('admin_shifts_customer_id');
            } else {
                localStorage.setItem('admin_shifts_customer_id', selectedCustomer);
            }
        }
    }, [selectedCustomer]);

    useEffect(() => {
        if (branchId !== undefined) {
            if (branchId === null || branchId === '') {
                localStorage.removeItem('admin_shifts_branch_id');
            } else {
                localStorage.setItem('admin_shifts_branch_id', branchId);
            }
        }
    }, [branchId]);

    useEffect(() => {
        if (startTime !== undefined) {
            if (startTime === null) {
                localStorage.removeItem('admin_shifts_start_time');
            } else {
                localStorage.setItem('admin_shifts_start_time', startTime.toISOString());
            }
        }
    }, [startTime]);

    useEffect(() => {
        if (endTime !== undefined) {
            if (endTime === null) {
                localStorage.removeItem('admin_shifts_end_time');
            } else {
                localStorage.setItem('admin_shifts_end_time', endTime.toISOString());
            }
        }
    }, [endTime]);

    useEffect(() => {
        if (selectedCustomer && customers.length > 0) {
            const customer = customers.find(customer => customer.id === selectedCustomer);
            if (customer) {
                const branches = customer?.branches?.map((branch) => {
                    return {id: branch.id, value: branch.id, label: branch.company_name}
                }) || [];

                setCustomerBranches(branches);

                const currentBranchId = getValues('branch_id');
                if (customer.branches && customer.branches.length === 1 && !currentBranchId) {
                    setValue('branch_id', customer.branches[0].id, { shouldValidate: true });
                }
            }
        } else if (!selectedCustomer) {
            setCustomerBranches([]);
        }
    }, [selectedCustomer, customers, setValue, getValues]);

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedCustomer, branchId, startTime, endTime]);

    useEffect(() => {
        getMyShifts(
            dayjs().unix(),
            selectedCustomer,
            startTime ? dayjs(startTime).unix() : null,
            endTime ? dayjs(endTime).unix() : null,
            [], null, branchId, currentPage, null, sortBy, sortOrder, statutenFilter
        );
    }, [getMyShifts, selectedCustomer, startTime, endTime, branchId, currentPage, sortBy, sortOrder, statutenFilter?.join(',')]);



    const columnHelper = createColumnHelper()
    const columns = [
        columnHelper.accessor('start_time', {
            header: t('tables.start'),
            cell: props => dayjs(props.cell.getValue()).format('DD/MM/YYYY HH:mm'),
            enableSorting: true,
        }),
        columnHelper.accessor('end_time', {
            header: t('tables.end'),
            cell: props => dayjs(props.cell.getValue()).format('DD/MM/YYYY HH:mm'),
            enableSorting: true,
        }),
        columnHelper.accessor('is_published', {
            header: t('tables.published'),
            cell: props => props.cell.getValue() ? t('general.labels.yes') : t('general.labels.no'),
            enableSorting: false,
        }),
        columnHelper.accessor('branch', {
            header: t('tables.customer'),
            cell: props => truncateString(`${props.cell.getValue()?.company_name ?? '-'}`),
            enableSorting: false,
        }),
        columnHelper.accessor('freelancer', {
            header: t('shifts_module.column_freelancer'),
            cell: props => truncateString(`${props.cell.getValue()?.first_name ?? '-'} ${props.cell.getValue()?.last_name ?? ''}`),
            enableSorting: false,
        }),
        columnHelper.accessor('allowed_statuten', {
            header: t('shifts_module.column_allowed_statuten'),
            enableSorting: false,
            cell: props => {
                const list = props.cell.getValue()?.length ? props.cell.getValue() : ['freelance'];
                const abbr = {freelance: 'F', interim: 'I', student: 'S', flexi_job: 'X'};
                return list.map(s => abbr[s] ?? s).join(' / ');
            },
        }),
        columnHelper.accessor('confirmed_applied_as_statuut', {
            header: t('shifts_module.column_confirmed_statuut'),
            enableSorting: false,
            cell: props => {
                const s = props.cell.getValue();
                return s ? t(`statuten.${s}`, s) : '-';
            },
        }),
        columnHelper.accessor('actions', {
            header: t('tables.actions'),
            enableSorting: false,
            cell: props => {
                return (
                    <div className={'flex flex-row space-x-4'}>
                        <Link
                            to={`/admin/shifts/${props.row.original.id}`}
                            className={'flex bg-primary w-7 h-7 rounded-full text-white flex-col justify-center border border-primary hover:bg-transparent hover:border-primary hover:text-primary transition-all duration-300'}
                        >
                            <FontAwesomeIcon icon={faArrowRight} className={'text-sm'}/>
                        </Link>
                    </div>
                )
            },
        })
    ];

    return (
        <div>
            <BaseCard border={false} className={'mb-4'}>
                <div className={'grid grid-cols-1 gap-4 md:grid-cols-5'}>
                    <div>
                        <SelectInput
                            name={'customer_id'}
                            options={customers.map((customer) => {
                                return {id: customer?.id, value: customer?.id, label: `${customer?.firstname} ${customer?.lastname}`}
                            })}
                            clearable={true}
                            register={register}
                            control={control}
                            setValue={(fieldName, value) => {
                                if (fieldName === 'customer_id') {
                                    setCustomerBranches([]);
                                    setValue('branch_id', null);
                                    setValue('customer_id', value, { shouldValidate: true });
                                }
                            }}
                            placeholder={t('general.fields.client')}
                            translationIndex={'shifts.client'}
                        />
                    </div>

                    <div>
                        <SelectInput
                            name={'branch_id'}
                            options={customerBranches}
                            register={register}
                            control={control}
                            setValue={setValue}
                            translationIndex={t('tables.branch')}
                            clearable={true}
                        />
                    </div>

                    <div>
                        <SelectInput
                            name={'statuten_filter'}
                            options={statutenOptions}
                            multiple={true}
                            control={control}
                            setValue={setValue}
                            currentValue={statutenFilter ?? []}
                            translationIndex={'shifts.allowed_statuten'}
                        />
                    </div>

                    <div>
                        <DateTimePickerInput
                            label={t('general.fields.start_time')}
                            name={'start_time'}
                            register={register}
                            control={control}
                            setValue={setValue}
                            dateFormat={'dd/MM/yyyy'}
                            showTimeInput={false}
                            isClearable={true}
                            translationIndex={'general.fields.start_time'}
                            errors={errors}
                        />
                    </div>

                    <div>
                        <DateTimePickerInput
                            name={'end_time'}
                            dateFormat={'dd/MM/yyyy'}
                            register={register}
                            control={control}
                            setValue={setValue}
                            showTimeInput={false}
                            isClearable={true}
                            translationIndex={'general.fields.end_time'}
                            errors={errors}
                        />
                    </div>
                </div>

                {selectedCustomer && (
                    <div className={'flex justify-end mt-4'}>
                        <BlackButton onClick={() => {
                            if(!branchId){
                                toast.error(t('shifts.select_branch_first'));
                                return;
                            }
                            setOpen(true)
                        }}>{t('shifts.add_shift')}</BlackButton>
                    </div>
                )}
            </BaseCard>

            <BaseCard>
                {shiftsPaginationMeta?.total !== undefined && (
                    <div className={'mb-4'}>
                        <p className={'text-sm text-gray-600'}>
                            {t('shifts.total_count', {count: shiftsPaginationMeta.total})}
                        </p>
                    </div>
                )}
                <TanStackTable
                    columns={columns}
                    tableData={shifts}
                    laravelPaginationMetaData={shiftsPaginationMeta}
                    onChangePage={(page) => {
                        setCurrentPage(page + 1);
                    }}
                    onSortingChange={(columnId, order) => {
                        setSortBy(columnId);
                        setSortOrder(order);
                        setCurrentPage(1);
                    }}
                />
            </BaseCard>

            <AdminCreateShiftDialog
                open={open}
                onClose={() => setOpen(false)}
                customerId={selectedCustomer ?? null}
                branchId={branchId ?? null}
                customerBranches={customerBranches}
                onCreated={() => {
                    getMyShifts(
                        dayjs().unix(),
                        selectedCustomer,
                        startTime ? dayjs(startTime).unix() : null,
                        endTime ? dayjs(endTime).unix() : null,
                        [], null, branchId, currentPage
                    );
                }}
            />
        </div>
    )
}